import type {
  AdElement,
  AdTemplateDisplayOptions,
  Animation,
  ElementPatch,
  HistoryEntry,
} from '@adflow/types';

/** The slice of the editor state that history entries read and write. */
export type HistoryDocument = {
  elements: AdElement[];
  animation: Animation | null;
  /** Only entries that change template display options need it. */
  displayOptions?: AdTemplateDisplayOptions;
};

export type AppliedHistoryEntry = {
  document: HistoryDocument;
  /** Reverses what was just applied, built from the document as it was before. */
  inverse: HistoryEntry;
};

/** What the canvas needs from the store-owned undo history. */
export type EditorCanvasHistory = {
  undoDepth: number;
  redoDepth: number;
  undo: () => void;
  redo: () => void;
  /** One finished drag. `before` and `after` hold the x/y of every element that moved. */
  onMoveCommit: (
    before: Record<string, { x: number; y: number }>,
    after: Record<string, { x: number; y: number }>,
  ) => void;
};

/** Undo depth is capped so a long session cannot grow without bound. */
export const MAX_HISTORY_DEPTH = 200;

function applySetPositions(
  document: HistoryDocument,
  positions: Record<string, { x: number; y: number }>,
): AppliedHistoryEntry {
  const previous: Record<string, { x: number; y: number }> = {};
  const elements = document.elements.map((element) => {
    const target = positions[element.id];
    if (!target) {
      return element;
    }
    previous[element.id] = { x: element.position.x, y: element.position.y };
    return { ...element, position: { ...element.position, x: target.x, y: target.y } };
  });
  return {
    document: { ...document, elements },
    inverse: { type: 'setPositions', positions: previous },
  };
}

function applySetZ(document: HistoryDocument, z: Record<string, number>): AppliedHistoryEntry {
  const previous: Record<string, number> = {};
  const elements = document.elements.map((element) => {
    const target = z[element.id];
    if (target === undefined) {
      return element;
    }
    previous[element.id] = element.position.z;
    return { ...element, position: { ...element.position, z: target } };
  });
  return {
    document: { ...document, elements },
    inverse: { type: 'setZ', z: previous },
  };
}

function applyRemoveElements(document: HistoryDocument, ids: string[]): AppliedHistoryEntry {
  const idSet = new Set(ids);
  const items: Array<{ element: AdElement; index: number }> = [];
  const elements: AdElement[] = [];
  document.elements.forEach((element, index) => {
    if (idSet.has(element.id)) {
      items.push({ element, index });
    } else {
      elements.push(element);
    }
  });

  const animation = document.animation;
  const tracks = animation?.tracks.filter((track) => idSet.has(track.targetId)) ?? [];
  const allEffects = animation?.effects ?? [];
  const effects = allEffects.filter((effect) => idSet.has(effect.targetId));

  let nextAnimation = animation;
  if (animation && (tracks.length > 0 || effects.length > 0)) {
    const remainingEffects = allEffects.filter((effect) => !idSet.has(effect.targetId));
    nextAnimation =
      effects.length > 0 && remainingEffects.length === 0
        ? null
        : {
            ...animation,
            tracks: animation.tracks.filter((track) => !idSet.has(track.targetId)),
            effects: animation.effects ? remainingEffects : animation.effects,
          };
  }

  return {
    document: { ...document, elements, animation: nextAnimation },
    inverse: {
      type: 'insertElements',
      items,
      tracks,
      effects,
      timeline: animation?.timeline ?? null,
    },
  };
}

function applyInsertElements(
  document: HistoryDocument,
  entry: Extract<HistoryEntry, { type: 'insertElements' }>,
): AppliedHistoryEntry {
  const elements = [...document.elements];
  const existing = new Set(elements.map((element) => element.id));
  const inserted: string[] = [];
  [...entry.items]
    .sort((a, b) => a.index - b.index)
    .forEach(({ element, index }) => {
      if (existing.has(element.id)) {
        return;
      }
      elements.splice(Math.min(index, elements.length), 0, element);
      existing.add(element.id);
      inserted.push(element.id);
    });

  let animation = document.animation;
  if (entry.tracks.length > 0 || entry.effects.length > 0) {
    const base: Animation | null =
      animation ?? (entry.timeline ? { timeline: entry.timeline, tracks: [] } : null);
    if (base) {
      const knownEffects = new Set((base.effects ?? []).map((effect) => effect.id));
      const restoredEffects = entry.effects.filter((effect) => !knownEffects.has(effect.id));
      animation = {
        ...base,
        tracks: [...base.tracks, ...entry.tracks],
        ...(base.effects || restoredEffects.length > 0
          ? { effects: [...(base.effects ?? []), ...restoredEffects] }
          : {}),
      };
    }
  }

  return {
    document: { ...document, elements, animation },
    inverse: { type: 'removeElements', ids: inserted },
  };
}

/** Writes `fields` over `data`; a field set to `undefined` is removed. */
function withFields(data: Record<string, unknown>, fields: Record<string, unknown>) {
  const next = { ...data, ...fields };
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) {
      delete next[key];
    }
  }
  return next;
}

function applyPatchElements(
  document: HistoryDocument,
  patches: Record<string, ElementPatch>,
): AppliedHistoryEntry {
  const previous: Record<string, ElementPatch> = {};
  const elements = document.elements.map((element) => {
    const patch = patches[element.id];
    if (!patch) {
      return element;
    }
    const { data: fields, ...whole } = patch;
    const before: Record<string, unknown> = {};
    for (const key of Object.keys(whole) as Array<keyof typeof whole>) {
      before[key] = element[key];
    }
    let next = { ...element, ...whole } as AdElement;
    if (fields) {
      const data = element.data as Record<string, unknown>;
      before.data = Object.fromEntries(Object.keys(fields).map((key) => [key, data[key]]));
      next = { ...next, data: withFields(data, fields) } as AdElement;
    }
    previous[element.id] = before as ElementPatch;
    return next;
  });
  return {
    document: { ...document, elements },
    inverse: { type: 'patchElements', patches: previous },
  };
}

function applySetDisplayOptions(
  document: HistoryDocument,
  values: Partial<AdTemplateDisplayOptions>,
): AppliedHistoryEntry {
  const current = document.displayOptions;
  if (!current) {
    return { document, inverse: { type: 'setDisplayOptions', values: {} } };
  }
  const previous: Record<string, unknown> = {};
  for (const key of Object.keys(values) as Array<keyof AdTemplateDisplayOptions>) {
    previous[key] = current[key];
  }
  return {
    document: { ...document, displayOptions: { ...current, ...values } },
    inverse: {
      type: 'setDisplayOptions',
      values: previous as Partial<AdTemplateDisplayOptions>,
    },
  };
}

/**
 * Performs one history entry and returns the entry that reverses it. Pure: the input document is
 * never mutated. Because the inverse is read from the document at apply time, edits that are not
 * recorded (typing into a text element, say) survive an undo followed by a redo, as long as they
 * touch other fields than the recorded step (element keys, or fields inside `data`).
 */
export function applyHistoryEntry(
  document: HistoryDocument,
  entry: HistoryEntry,
): AppliedHistoryEntry {
  switch (entry.type) {
    case 'setPositions':
      return applySetPositions(document, entry.positions);
    case 'setZ':
      return applySetZ(document, entry.z);
    case 'removeElements':
      return applyRemoveElements(document, entry.ids);
    case 'insertElements':
      return applyInsertElements(document, entry);
    case 'patchElements':
      return applyPatchElements(document, entry.patches);
    case 'setAnimation':
      return {
        document: { ...document, animation: entry.animation },
        inverse: { type: 'setAnimation', animation: document.animation },
      };
    case 'setDisplayOptions':
      return applySetDisplayOptions(document, entry.values);
    case 'batch': {
      let current = document;
      const inverses: HistoryEntry[] = [];
      for (const child of entry.entries) {
        const applied = applyHistoryEntry(current, child);
        current = applied.document;
        inverses.push(applied.inverse);
      }
      return {
        document: current,
        inverse: { type: 'batch', entries: inverses.reverse() },
      };
    }
    default:
      return { document, inverse: entry };
  }
}

/**
 * Combines the reversing entries recorded during a batch into one undo step. They are stored in
 * the order the actions happened, so they have to be applied back to front.
 */
export function combineHistoryEntries(recorded: HistoryEntry[]): HistoryEntry | null {
  if (recorded.length === 0) {
    return null;
  }
  if (recorded.length === 1) {
    return recorded[0];
  }
  return { type: 'batch', entries: [...recorded].reverse() };
}

/**
 * Folds the reversing entry of a newer edit into the one of an older edit, so one undo restores
 * the state from before both. Where both cover the same field, the older value wins.
 */
export function mergeHistoryEntries(older: HistoryEntry, newer: HistoryEntry): HistoryEntry {
  if (older.type === 'patchElements' && newer.type === 'patchElements') {
    const patches: Record<string, ElementPatch> = { ...newer.patches };
    for (const [id, patch] of Object.entries(older.patches)) {
      const newerPatch = newer.patches[id];
      patches[id] = { ...newerPatch, ...patch };
      if (newerPatch?.data && patch.data) {
        patches[id].data = { ...newerPatch.data, ...patch.data };
      }
    }
    return { type: 'patchElements', patches };
  }
  if (older.type === 'setDisplayOptions' && newer.type === 'setDisplayOptions') {
    return { type: 'setDisplayOptions', values: { ...newer.values, ...older.values } };
  }
  if (older.type === 'setAnimation' && newer.type === 'setAnimation') {
    return older;
  }
  return { type: 'batch', entries: [newer, older] };
}

export function pushHistory(stack: HistoryEntry[], entry: HistoryEntry): HistoryEntry[] {
  const next = [...stack, entry];
  return next.length > MAX_HISTORY_DEPTH ? next.slice(next.length - MAX_HISTORY_DEPTH) : next;
}

/** True for the entry `insertNewElement` records, which removes exactly one element. */
export function isRemoveOnly(entry: HistoryEntry, id: string): boolean {
  return entry.type === 'removeElements' && entry.ids.length === 1 && entry.ids[0] === id;
}
